import React from 'react';
import Dialog from 'pages/Dashboard/components/Dialog';
import noop from 'lodash/noop';
import {
  useNotify,
} from 'core/hooks';
import {
  Controller,
  FormProvider,
  useForm,
} from 'react-hook-form';
import Button from 'pages/Dashboard/components/Button';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import {
  getSubmitButtonText,
  isEmptyString,
} from '@populateui/utils';
import isNil from 'lodash/isNil';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  BillingProviderInfo,
} from 'views/EMR/Settings/BillingConfigurations/types';
import {
  useUpsertBillingProvider,
} from 'views/EMR/Settings/hooks';
import Input from 'pages/Dashboard/components/Input';
import NavigationTabs from 'pages/Dashboard/components/NavigationTabs';
import NPI from 'views/EMR/Settings/BillingConfigurations/NPI';
import {
  addressFields,
  validateAddressWithLongZipCode,
} from 'utils/react-hook-form';
import pick from 'lodash/pick';
import AddressFields from 'pages/Dashboard/components/AddressFields';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import PhoneInput from 'core/components/PhoneInput';
import omit from 'lodash/omit';
import Select from 'core/components/Select';
import MaskedTextField from 'core/components/MaskedTextField';
import {
  normalizePhoneNumber,
  validatePhone,
} from 'pages/Dashboard/utils/helper';

export type BillingProviderDialogProps = {
  billingProvider?: BillingProviderInfo | null;
};

type Props = BillingProviderDialogProps & {
  onClose: () => void;
};

const billingProviderTypes = ['Individual', 'Org'] as const;
type BillingProviderType = typeof billingProviderTypes[number];

export type BillingProviderTypeOption = {
  label: string;
  key: BillingProviderType;
  index: number;
};

export const billingProviderTypeOptions: BillingProviderTypeOption[] = [
  { label: 'Individual', key: 'Individual', index: 0 },
  { label: 'Organization', key: 'Org', index: 1 },
];

const defaultValues: BillingProviderInfo = {
  type: 'Individual',
};

const organizationProviderKeys = ['name'];
const individualProviderKeys = ['firstName', 'lastName', 'credentials'];

export default function BillingProviderDialog({
  onClose,
  billingProvider,
}: Props) {
  const isNew = isNil(billingProvider);
  const formProps = useForm<BillingProviderInfo>({
    mode: 'onChange',
    defaultValues: billingProvider ?? defaultValues,
  });

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { isValid, isSubmitting },
    setValue,
  } = formProps;

  const type = watch('type');
  const isIndividual = /individual/i.test(type ?? '');

  const { mutateAsync: upsertBillingProvider } = useUpsertBillingProvider();
  const notify = useNotify();
  const userInfo = useUserInfo();

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async ({ credentials, ...data }: BillingProviderInfo) => {
    try {
      await upsertBillingProvider({
        ...omit(data, isIndividual ? organizationProviderKeys : individualProviderKeys),
        credentials: isEmptyString(credentials) ? undefined : credentials,
        organizationId: userInfo?.organization?.organizationId,
        phoneNumber: isEmptyString(data.phoneNumber)
          ? undefined : normalizePhoneNumber(data.phoneNumber ?? ''),
      });
      notify({
        variant: 'success',
        message: `Billing Provider ${isNew ? 'created' : 'updated'} successfully`,
      });
      settingsEventChannel.emit('onBillingProviderChange');
      handleClose();
    } finally {
      reset(data, { keepIsSubmitted: false });
    }
  };

  return (
    <FormProvider {...formProps}>
      <Dialog
        title={(
          <div className="flex flex-1 flex-col gap-4">
            <h2 className="text-lg font-semibold">
              {`${isNew ? 'Add' : 'Edit'} Billing Provider`}
            </h2>
            {isNew && (
              <NavigationTabs
                className="flex"
                tabs={billingProviderTypeOptions}
                activeIndex={Number(!isIndividual)}
                onClick={(active) => {
                  setValue('type', billingProviderTypes[active]);
                }}
              />
            )}
          </div>
        )}
        closeBtnClasses="!-top-1"
        onClose={onClose}
        onConfirm={noop}
        className="!w-176 populate-form"
        maxWidth="lg"
        open
      >
        <div className="flex flex-col gap-form-row">
          <div className="flex flex-col gap-form-row">
            {isIndividual ? (
              <div className="flex gap-form-field">
                <Controller
                  name="firstName"
                  control={control}
                  rules={{ required: 'First Name is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      {...field}
                      error={!isNil(error)}
                      showErrorText={false}
                      className="w-full text-gray-500 text-xs"
                      id="first-name"
                      label="First Name"
                      placeholder="First Name"
                      inputProps={{ maxLength: 64 }}
                      required
                    />
                  )}
                />
                <Controller
                  name="lastName"
                  control={control}
                  rules={{ required: 'Last Name is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      {...field}
                      error={!isNil(error)}
                      showErrorText={false}
                      className="w-full text-gray-500 text-xs"
                      id="last-name"
                      label="Last Name"
                      placeholder="Last Name"
                      inputProps={{ maxLength: 64 }}
                      required
                    />
                  )}
                />
                <Controller
                  name="credentials"
                  control={control}
                  render={({ field, fieldState: { invalid } }) => (
                    <Input
                      {...field}
                      error={invalid}
                      showErrorText={false}
                      className="w-full text-gray-500 text-xs"
                      id="credentials-name"
                      label="Credentials"
                      placeholder="E.g. DPM"
                      inputProps={{ maxLength: 8 }}
                    />
                  )}
                />
              </div>
            ) : (
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Name is required' }}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    {...field}
                    error={!isNil(error)}
                    showErrorText={false}
                    className="w-full text-gray-500 text-xs"
                    id="last-name"
                    label="Name"
                    placeholder="Enter Name of the Organization"
                    inputProps={{ maxLength: 64 }}
                    required
                  />
                )}
              />
            )}
            <div className="flex flex-col">
              <Controller
                name="address"
                control={control}
                rules={{
                  required: 'Address is required',
                  validate: (value) => (validateAddressWithLongZipCode(pick(value, addressFields)) || 'Enter valid address'),
                }}
                render={({ field, fieldState: { invalid } }) => (
                  <AddressFields
                    {...field}
                    isLongZipRequired
                    required
                    error={invalid}
                  />
                )}
              />
            </div>
            <div className="flex gap-form-field">
              <Controller
                name="phoneNumber"
                control={control}
                rules={{
                  required: 'Phone Number is required',
                  validate: (value) => (validatePhone(value) || 'Provide valid phone number'),
                }}
                render={({ field, fieldState: { error } }) => (
                  <Wrapper
                    className="flex-1 min-w-0"
                    error={!isNil(error)}
                    label="Phone number"
                    id="contact-phone"
                    showErrorText={false}
                    required
                  >
                    <PhoneInput
                      {...field}
                      error={!isNil(error)}
                      id="phone"
                      name="phone"
                    />
                  </Wrapper>
                )}
              />
              <Controller
                name="contact"
                control={control}
                rules={{ required: 'Contact is required' }}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    {...field}
                    error={!isNil(error)}
                    showErrorText={false}
                    className="flex-1 min-w-0 text-gray-500 text-xs"
                    id="contact-name"
                    label="Contact"
                    inputProps={{ maxLength: 64 }}
                    required
                  />
                )}
              />
            </div>
            <NPI required />
            <div className="flex gap-form-field">
              <Controller
                name="taxId.type"
                control={control}
                rules={{ required: 'Tax ID type is required' }}
                render={({ field, fieldState: { invalid } }) => (
                  <Select<string>
                    {...field}
                    error={invalid}
                    showErrorText={false}
                    id="tax-id-type"
                    className="flex-1 min-w-0 text-gray-500 text-xs"
                    label="Tax ID Type"
                    placeholder="Select"
                    options={['EIN', 'SSN']}
                    getLabel={(option: string) => option}
                    getValue={(option: string) => option}
                    required
                  />
                )}
              />
              <Controller
                name="taxId.number"
                control={control}
                rules={{
                  required: 'Tax ID is required',
                  validate: (value) => (value?.length === 9 || 'Tax ID must be 9 digits'),
                }}
                render={({ field, fieldState: { invalid } }) => (
                  <Wrapper
                    id="tax-id-number"
                    label="Tax ID"
                    className="flex-1 min-w-0"
                    showErrorText={false}
                    required
                    error={invalid}
                  >
                    <MaskedTextField
                      {...field}
                      unmask
                      mask={[{
                        mask: '000000000',
                        lazy: false,
                        overwrite: true,
                      }]}
                      placeholder="Tax ID"
                      onAccept={field.onChange}
                    />
                  </Wrapper>
                )}
              />
            </div>
          </div>
          <div className="flex flex-row-reverse gap-2 justify-between">
            <DisabledFormTooltip>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!isValid || isSubmitting}
                onClick={handleSubmit(onSubmit)}
              >
                {getSubmitButtonText(isNew, isSubmitting)}
              </Button>
            </DisabledFormTooltip>
            <Button
              variant="outlined"
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Close
            </Button>
          </div>
        </div>
      </Dialog>
    </FormProvider>
  );
}
